import { useEffect, useRef, useState } from 'react';

export type MenuAction = { label: string; run: () => void; danger?: boolean; disabled?: boolean };
export function ActionMenu({ label, actions }: { label: string; actions: MenuAction[] }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role=menuitem]:not(:disabled)')?.focus();
    const dismiss = (focus = false) => {
      setOpen(false);
      if (focus) trigger.current?.focus();
    };
    const pointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) dismiss();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        dismiss(true);
      }
      if (e.key === 'Tab') dismiss();
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
        const items = [...root.current!.querySelectorAll<HTMLButtonElement>('[role=menuitem]:not(:disabled)')];
        if (!items.length) return;
        const index = items.indexOf(document.activeElement as HTMLButtonElement);
        items[
          e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? items.length - 1
              : (index + (e.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length
        ]?.focus();
      }
    };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);
  return (
    <div ref={root} className="sp-card-menu">
      <button
        ref={trigger}
        className="sp-icon"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        •••
      </button>
      {open && (
        <div className="sp-menu" role="menu" aria-label={label} style={{ position: 'absolute', right: 0, top: 42 }}>
          {actions.map((action) => (
            <button
              role="menuitem"
              key={action.label}
              disabled={action.disabled}
              className={action.danger ? 'sp-danger-text' : ''}
              onClick={() => {
                setOpen(false);
                trigger.current?.focus();
                action.run();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
